import React, { useCallback } from "react"
import { type FieldError, useFormContext, type RegisterOptions } from "react-hook-form"
import { useTranslation } from "react-i18next"
import Input, { type InputProps } from "./input"
import { dig } from "@/common/util/misc"

export interface FormInputs {
  [key: string]: string | boolean | undefined | null
}

export interface FormInputProps extends InputProps {
  name: string
  rules?: RegisterOptions
  errorInterpolation?: object
}

const ForwardableFormInput: React.ForwardRefRenderFunction<HTMLInputElement, FormInputProps> = (
  { rules, name, errorInterpolation, ...props },
  ref
) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<FormInputs>()
  const error = dig(errors, name) as FieldError | undefined
  const registerFields = register(name, rules)

  const { t } = useTranslation()
  const tOptions: Record<string, string> = { ...errorInterpolation }
  const errorMessage = error && (error.message ? t(error.message, tOptions) : t("misc.required_field"))

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      registerFields.onChange(e)

      if (props.onChange) props.onChange(e)
    },
    [props, registerFields]
  )

  return (
    <Input {...props} registerFields={registerFields} name={name} onChange={onChange} error={errorMessage} ref={ref} />
  )
}

const FormInput = React.forwardRef(ForwardableFormInput)

export default FormInput
